/* One material.
   OWNED BY ITEM 4 OF docs/plan/00-prestavba-2026-08.md.

   Two things this page owes the reader, and neither was there before:

     it opens with what comes out, not with a description of the target state.
     A teacher decides from the artefact, not from a promise about it.

     it is condensed. Twenty-five blocks read top to bottom are right; scanned,
     they are a wall — and scanning is what someone does while deciding whether
     this is the example they wanted. So the headings are the page and the
     explanation opens when it is asked for. */

import { useParams } from "react-router-dom";
import { covers } from "../../lib/covers";
import { useI18n } from "../../lib/i18n";
import { usePortal } from "../../lib/portal";
import { useDoc, useIndex, itemsIn } from "../../lib/hooks";
import { Billboard, Chip, Crumbs, Row, Tile } from "./Tile";
import { Folds } from "../Folds";
import { LoadFailed } from "../NotFound";
import { Untranslated } from "../Untranslated";
import { preview, sections } from "./sections";
import { TileFoot, tileSub } from "./entry";
import { LessonBody } from "../lesson/LessonPage";
import { isLesson } from "../../lib/types";

export function MaterialPage() {
  const { id } = useParams();
  const { t, ui, href } = useI18n();
  const { taxonomy } = usePortal();
  const { data: doc, error, loading } = useDoc(id);
  const { data: index } = useIndex();

  if (loading) return null;
  if (error || !doc) return <LoadFailed error={error} />;

  const meta = doc.meta ?? {};
  const strand = meta.strand?.[0];
  const s = strand ? taxonomy?.strands[strand] : undefined;
  const sub = meta.subject ? taxonomy?.subjects[meta.subject] : undefined;

  /* A lesson opens with its goal; a worked example opens with what came out of
     it. Everything around the two — the crumbs, the billboard, what else there
     is in the subject — is the same page. */
  const lesson = isLesson(doc) ? doc : null;
  const shown = lesson ? null : preview(doc.blocks);
  const parts = lesson ? [] : sections(doc.blocks);

  const related = index && strand && meta.subject
    ? itemsIn(index, strand, meta.subject).filter((e) => e.id !== doc.id)
    : [];

  /* The cover art takes its palette from the strand and its density from the
     year inside that strand's span. This page is not a column of the catalogue,
     so the first of the material's strands decides — the same one the crumbs
     above lead back to. */
  const branch = strand ? { strand, years: s?.years } : undefined;

  return (
    <div className="catalog">
      <Crumbs parts={[
        { label: ui("nav.home"), to: href("/") },
        ...(s && strand ? [{ label: t(s.short), to: href(`/s/${strand}`) }] : []),
        ...(sub && meta.subject && strand
          ? [{ label: t(sub.name), to: href(`/s/${strand}/${meta.subject}`) }] : []),
        ...(strand && meta.subject && meta.year
          ? [{ label: `${meta.year}. ${ui("year.suffix")}`,
              to: href(`/s/${strand}/${meta.subject}/${meta.year}`) }] : []),
        { label: t(doc.title) },
      ]} />

      <Billboard
        art={covers.material(
          { id: doc.id, subject: meta.subject, year: meta.year }, branch)}
        eyebrow={sub ? t(sub.name) : undefined}
        title={t(doc.title)}
        lead={t(doc.claim)}
        meta={
          <>
            {lesson ? <Chip>{ui("catalog.lesson")}</Chip> : null}
            {meta.grade ? <Chip>{t(meta.grade)}</Chip> : null}
            {s ? <Chip>{t(s.short)}</Chip> : null}
          </>
        }
      />

      <Untranslated doc={doc} />

      {shown ? (
        <section className="cat-preview">
          <h2 className="cat-preview-title">
            {ui("material.preview")}
            {shown.from ? <span className="cat-preview-from">{t(shown.from)}</span> : null}
          </h2>
          <div className="out">{t(shown.out)}</div>
        </section>
      ) : null}

      {lesson ? <LessonBody doc={lesson} /> : <Folds parts={parts} />}

      {related.length ? (
        <Row title={ui("material.more")}>
          {related.map((e) => (
            <Tile key={e.id} to={href(`/m/${e.id}`)} art={covers.material(e, branch)}
                  title={e.title} sub={tileSub(e)} foot={<TileFoot entry={e} />} />
          ))}
        </Row>
      ) : null}
    </div>
  );
}
